import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import fscreen from "fscreen";
import { metricsBus } from "@/common/buses";

interface FullScreenOptions {
  targetRef?: RefObject<HTMLElement | null>;
}

const isPromiseLike = (value: unknown): value is Promise<unknown> =>
  typeof value === "object" &&
  value !== null &&
  "then" in value &&
  typeof (value as Promise<unknown>).then === "function";

export const useEmbedFullScreen = ({ targetRef }: FullScreenOptions = {}) => {
  const isBrowser = typeof window !== "undefined";
  const isSupported = isBrowser && !!fscreen.fullscreenEnabled;

  const initialState = isBrowser && fscreen.fullscreenElement !== null;
  const [isFullscreen, setIsFullscreen] = useState<boolean>(initialState);

  const lastKnownStateRef = useRef<boolean>(initialState);

  const resolveTarget = useCallback((): HTMLElement | null => {
    if (!isBrowser) {
      return null;
    }

    if (targetRef?.current) {
      return targetRef.current;
    }

    if (document.fullscreenElement instanceof HTMLElement) {
      return document.fullscreenElement;
    }

    return document.body;
  }, [isBrowser, targetRef]);

  const handleFullscreenChange = useCallback(() => {
    if (!isBrowser) {
      return;
    }

    const nextIsFullscreen = fscreen.fullscreenElement !== null;
    setIsFullscreen(nextIsFullscreen);

    if (lastKnownStateRef.current !== nextIsFullscreen) {
      metricsBus.emit(nextIsFullscreen ? "fullscreenEntered" : "fullscreenExited");
      lastKnownStateRef.current = nextIsFullscreen;
    }
  }, [isBrowser]);

  const handleFullscreenError = useCallback((e?: Event) => {
    console.debug("Fullscreen Error", e);
  }, []);

  useEffect(() => {
    if (!isSupported) {
      return () => undefined;
    }

    fscreen.addEventListener("fullscreenchange", handleFullscreenChange, false);
    fscreen.addEventListener("fullscreenerror", handleFullscreenError, false);

    return () => {
      fscreen.removeEventListener("fullscreenchange", handleFullscreenChange);
      fscreen.removeEventListener("fullscreenerror", handleFullscreenError);
    };
  }, [handleFullscreenChange, handleFullscreenError, isSupported]);

  const enterFullscreen = useCallback(() => {
    if (!isSupported) {
      return;
    }

    const target = resolveTarget();

    if (!target) {
      console.warn("Fullscreen target element is not available.");
      return;
    }

    try {
      const result = fscreen.requestFullscreen(target);

      if (isPromiseLike(result)) {
        result.catch((error: Error) => {
          console.error(`Could not enter fullscreen: ${error}`);
        });
      }
    } catch (error) {
      console.error(`Could not enter fullscreen: ${error}`);
    }
  }, [isSupported, resolveTarget]);

  const exitFullscreen = useCallback(() => {
    if (!isSupported) {
      return;
    }

    if (!fscreen.fullscreenElement) {
      return;
    }

    try {
      const result = fscreen.exitFullscreen();

      if (isPromiseLike(result)) {
        result.catch((error: Error) => {
          console.error(`Could not exit fullscreen: ${error}`);
        });
      }
    } catch (error) {
      console.error(`Could not exit fullscreen: ${error}`);
    }
  }, [isSupported]);

  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  }, [enterFullscreen, exitFullscreen, isFullscreen]);

  return useMemo(
    () => ({
      isSupported,
      isFullscreen,
      enterFullscreen,
      exitFullscreen,
      toggleFullscreen
    }),
    [enterFullscreen, exitFullscreen, isFullscreen, isSupported, toggleFullscreen]
  );
};
